import { FIGMA_EMBED_MESSAGE_TYPES } from '@/consts/figma-embed-messages';

/**
 * Ask the Figma parent to run its canonical `fullscreenActions.backToFiles`
 * flow. Parent handler validates origin + source before acting; see
 * `web/js/weave/weave_iframe_view.tsx` for the receiving side.
 *
 * `openInNewTab` mirrors the canonical Figma filename-menu "Back to files": when
 * the user holds Cmd/Ctrl, open the file browser in a new tab instead of
 * navigating the current one.
 */
export function postBackToFilesToParent(openInNewTab = false): void {
  try {
    window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.BACK_TO_FILES, openInNewTab }, '*');
  } catch {
    // parent postMessage blocked — give up silently.
  }
}

/**
 * Tell the Figma parent that the user picked a new cover photo for the open
 * file.
 * Noop when Weavy client isn't running inside an iframe.
 */
export function postSetCoverPhotoToFigmaParent(url: string): void {
  if (window.parent === window) {
    return;
  }
  try {
    window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.SET_FILE_COVER_PHOTO, url }, '*');
  } catch {
    // parent postMessage blocked — give up silently.
  }
}

/**
 * Ask the Figma parent to open its canonical sign-up/login modal — used by the
 * logged-out viewer's avatar / toolbar / Share / logo / title clicks.
 */
export function postRequestAuthModalToParent(): void {
  try {
    window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.REQUEST_AUTH_MODAL }, '*');
  } catch {
    // parent postMessage blocked — give up silently.
  }
}

/**
 * Report whether the top-right viewer toolbar (Share) is rendered, so Figma can
 * dock the logged-out sign-up banner only alongside it.
 */
export function postViewerToolbarVisibleToParent(visible: boolean): void {
  try {
    window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.VIEWER_TOOLBAR_VISIBLE, visible }, '*');
  } catch {
    // parent postMessage blocked — give up silently.
  }
}

/**
 * Tell the Figma parent the editor canvas has painted its first frame, so it can
 * drop its loading overlay and reveal chrome over real content (not a black/
 * initializing canvas). Noop outside an iframe.
 */
export function postCanvasReadyToParent(): void {
  if (window.parent === window) {
    return;
  }
  try {
    window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.CANVAS_READY }, '*');
  } catch {
    // parent postMessage blocked — give up silently.
  }
}

/**
 * Tell the Figma parent we're about to reload the iframe in place, so it can
 * re-raise its loading overlay before the canvas goes blank. Noop outside an
 * iframe.
 */
export function postReloadingToParent(): void {
  if (window.parent === window) {
    return;
  }
  try {
    window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.RELOADING }, '*');
  } catch {
    // parent postMessage blocked — give up silently.
  }
}
